"use client";

// Shared pieces of the free test cards: the card frame, buttons, the result
// call-to-action, the "can't be paused" guard and the keyboard response.
// Words come from messages/<locale>/common.json (useT, 2026-11-04).
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Share2 } from "lucide-react";
import { SITE, WEB_APP_LINKS } from "@/lib/constants";
import { FreeTestError, finishedRuns, trackTest, type FreeTestId } from "@/lib/freeTests";
import { useT } from "@/components/i18n/I18nProvider";
import type { Translator } from "@/lib/i18n/translate";
import { AppStoreBadges } from "@/components/shared/AppStoreBadges";
import { challengeImagePath, profileImagePath, profileShareUrl, shareScoreParam, shareUrl } from "@/lib/challenge";
import { ChallengeCompare } from "./Challenge";
import { cn } from "@/lib/utils";
import { useEmbed } from "./EmbedFrame";
import { track } from "@/lib/analytics";
import { SaveProgress } from "./SaveProgress";
import { interactions } from "@/lib/testInteractions";

export const nowMs = (): number =>
  typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

export const PRIMARY_BUTTON =
  "rounded-full bg-primary px-10 py-4 text-lg font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-opacity hover:opacity-90 disabled:opacity-50";
export const SECONDARY_BUTTON =
  "rounded-full border border-border bg-card px-8 py-3 font-semibold text-foreground transition-colors hover:bg-muted";

export function TestCard({ children, cardRef }: { children: ReactNode; cardRef?: React.Ref<HTMLDivElement> }) {
  return (
    <div
      ref={cardRef}
      className="scroll-mt-24 overflow-hidden rounded-2xl border border-border bg-card shadow-[var(--shadow-md)]"
    >
      {children}
    </div>
  );
}

/** The words for a failed service call (FreeTestError code), or the generic line. */
export function errorText(tr: Translator, e: unknown): string {
  return e instanceof FreeTestError ? tr.t(`common.error_${e.code}`) : tr.t("common.error_generic");
}

export function TestUnavailable() {
  const { t } = useT();
  return (
    <TestCard>
      <div className="px-6 py-14 text-center">
        <h3 className="text-2xl font-bold text-foreground">{t("common.unavailable_title")}</h3>
        <p className="mx-auto mt-3 max-w-md text-muted-foreground">{t("common.unavailable_body")}</p>
      </div>
    </TestCard>
  );
}

export function CenteredMessage({
  title,
  body,
  action,
}: {
  title: string;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <h3 className="text-2xl font-bold text-foreground">{title}</h3>
      {body && <div className="mt-3 max-w-md text-muted-foreground">{body}</div>}
      {action && <div className="mt-8">{action}</div>}
    </div>
  );
}

export function Scoring({ text }: { text?: string }) {
  const { t } = useT();
  return (
    <div className="flex flex-col items-center px-6 py-20 text-center" role="status" aria-live="polite">
      <span className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" aria-hidden />
      <p className="mt-4 text-muted-foreground">{text ?? t("common.scoring")}</p>
    </div>
  );
}

export function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-muted px-2 py-3">
      <dt className="text-xs leading-tight text-muted-foreground">{label}</dt>
      {/* <bdi>: "692 ms" / "100%" keep their order on right-to-left pages; an Arabic unit still reads right to left. */}
      <dd className="mt-1 whitespace-nowrap text-lg font-bold tabular-nums text-foreground">
        <bdi>{value}</bdi>
      </dd>
    </div>
  );
}

export function Headline({ label, value, unit }: { label: string; value: ReactNode; unit?: string }) {
  return (
    <>
      <p className="text-center text-sm font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-2 text-center text-6xl font-bold tracking-tight text-foreground sm:text-7xl">
        <bdi>
          {value}
          {unit && <span className="ms-1 text-3xl font-semibold text-muted-foreground sm:text-4xl">{unit}</span>}
        </bdi>
      </p>
    </>
  );
}

/**
 * The phone the page runs on, for the app block: iOS (iPadOS reports a Mac
 * with touch), Android, or null on a computer. Read after mount, so server and
 * first client render agree.
 */
function useMobilePlatform(): "ios" | "android" | null {
  const [platform, setPlatform] = useState<"ios" | "android" | null>(null);
  useEffect(() => {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) setPlatform("android");
    else if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) setPlatform("ios");
  }, []);
  return platform;
}

/**
 * The step from a result to the app, at the very bottom of every result.
 * `inApp`: the app has this same test, so "track this score" is literally
 * true; otherwise the block names the tests the app does have. On a phone the
 * store for that phone comes first; on a computer, the web app.
 */
export function AppBlock({ test, placement, inApp, title, body, copy }: { test?: string; placement: string; inApp: boolean; title?: string; body?: string; copy?: { title: string; body: string } }) {
  const embed = useEmbed();
  const platform = useMobilePlatform();
  const { t, locale } = useT();
  const clicked = (target: "app_store" | "google_play" | "web_signup") =>
    test ? trackTest("free_test_app_click", { test, target, platform: platform ?? "desktop" }, target === "web_signup")
      : track("study_tool_app_click", { tool: placement, target, platform: platform ?? "desktop" }, target === "web_signup");
  const onBadge = (store: "googlePlay" | "appStore") => clicked(store === "appStore" ? "app_store" : "google_play");
  if (embed) return null;
  return (
    <><SaveProgress test={test} /><div data-result-action="app" className="mx-auto mt-10 max-w-lg rounded-xl bg-primary-light px-6 py-6 text-center">
      <p className="text-lg font-semibold text-foreground">
        {title ?? copy?.title ?? t(inApp ? "common.app_title_in_app" : "common.app_title")}
      </p>
      <p className="mt-1 text-muted-foreground">{body ?? copy?.body ?? t(inApp ? "common.app_body_in_app" : "common.app_body")}</p>
      {locale !== "en" && <p className="mt-1 text-sm text-muted-foreground">{t("common.app_note")}</p>}
      {platform ? (
        <>
          <AppStoreBadges
            placement={placement}
            size="md"
            only={platform === "ios" ? "appStore" : "googlePlay"}
            onBadgeClick={onBadge}
            className="mt-5"
          />
          <a data-interaction="app" data-result-action="app"
            href={WEB_APP_LINKS.signUp}
            onClick={() => clicked("web_signup")}
            className="mt-3 inline-block text-sm font-medium text-primary hover:underline"
          >
            {t("common.app_in_browser")}
          </a>
        </>
      ) : (
        <>
          <a data-interaction="app" data-result-action="app"
            href={WEB_APP_LINKS.signUp}
            onClick={() => clicked("web_signup")}
            className="mt-5 inline-block rounded-full bg-primary px-8 py-3 font-semibold text-primary-foreground transition-opacity hover:opacity-90"
          >
            {t("common.app_start_browser")}
          </a>
          <AppStoreBadges placement={placement} size="sm" onBadgeClick={onBadge} className="mt-4" />
        </>
      )}
    </div></>
  );
}

/** Copy text with the async clipboard API, or the old select-and-copy way. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    area.remove();
    return ok;
  }
}

/**
 * "Share your score": the phone's share sheet where there is one (touch
 * screens), otherwise the text goes to the clipboard. The text is the score
 * line and the link to this test page on helibrain.com, nothing else. With a
 * `score` (the result's headline number) the link carries it, so the page it
 * opens shows the friend's score and a share image with it (lib/challenge.ts).
 */
export function ShareButton({
  test,
  line,
  score = null,
  seed = null,
  traits = null,
  label,
}: {
  test: FreeTestId;
  line: string;
  score?: number | null;
  seed?: number | null;
  /** A shared profile instead of a score (Big Five, lib/challenge.ts traitsParam). */
  traits?: string | null;
  /** A page-only share must not be labelled as sharing a personal score. */
  label?: string;
}) {
  const { t } = useT();
  const embed = useEmbed();
  const [status, setStatus] = useState<"idle" | "copied" | "manual">("idle");
  const [fallback, setFallback] = useState("");

  // Draw the score card now, so it's cached before a chat app asks for the link preview
  // (the first render takes seconds and link-preview bots give up quickly).
  useEffect(() => {
    if (embed) return;
    const path = window.location.pathname;
    const img = traits ? profileImagePath(path, traits) : score !== null ? challengeImagePath(path, test, score) : null;
    if (img) fetch(img, { keepalive: true }).catch(() => {});
  }, [test, score, traits, embed]);

  const share = async () => {
    const url = traits
      ? profileShareUrl(SITE.url, window.location.pathname, traits)
      : shareUrl(SITE.url, window.location.pathname, test, score, seed);
    const full = t("common.share_text", { line, url });
    const sheet =
      typeof navigator.share === "function" && window.matchMedia("(pointer: coarse)").matches;
    if (sheet) {
      try {
        await navigator.share({ text: t("common.share_sheet_text", { line }), url });
        trackTest("free_test_shared", { test, method: "share_sheet" });
        return;
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return; // closed the sheet
      }
    }
    if (await copyText(full)) {
      trackTest("free_test_shared", { test, method: "clipboard" });
      setStatus("copied");
      setTimeout(() => setStatus("idle"), 2500);
    } else {
      trackTest("free_test_shared", { test, method: "manual" });
      setFallback(full);
      setStatus("manual");
    }
  };

  if (embed) return <EmbedResultLink test={test} score={score} seed={seed} traits={traits} />;
  return (
    <div className="flex flex-col items-center">
      <button data-interaction="share" data-result-action="share" data-challenge-share={score !== null || seed !== null || Boolean(traits)} type="button" onClick={share} className={cn(SECONDARY_BUTTON, "inline-flex items-center gap-2")}>
        {status === "copied" ? <Check className="h-4 w-4" aria-hidden /> : <Share2 className="h-4 w-4" aria-hidden />}
        {status === "copied" ? t("common.copied") : label ?? t(traits ? "common.share_profile" : "common.share")}
      </button>
      <span className="sr-only" aria-live="polite">
        {status === "copied" ? t("common.copied_sr") : ""}
      </span>
      {status === "manual" && (
        <p className="mt-3 max-w-sm select-all rounded-lg bg-muted px-3 py-2 text-sm text-foreground">{fallback}</p>
      )}
    </div>
  );
}

function EmbedResultLink({ test, score, seed = null, traits = null }: { test: FreeTestId; score: number | null; seed?: number | null; traits?: string | null }) {
  const path = useEmbed()!;
  const { t } = useT();
  const url = new URL(traits ? profileShareUrl(SITE.url, path, traits) : shareUrl(SITE.url, path, test, score, seed));
  url.searchParams.set("from_embed", "1");
  url.hash = "shared-result";
  return <a href={url.toString()} target="_blank" rel="noopener" data-result-action="full-result" data-interaction="full-result"
    onClick={() => trackTest("free_test_full_result_opened", { test })}
    className="inline-flex max-w-full items-center justify-center rounded-full bg-primary px-6 py-3 text-center font-semibold text-primary-foreground">{t("embed.full_result")}</a>;
}

/**
 * Under every result, in this order: "Share your score" and "Try again", then
 * the app block at the bottom. (A "compared with others" line, once our own
 * stored results allow one, belongs in the result above this, under the score.)
 * `shareLine`: the score in one sentence ("My reaction time: 241 ms."); null
 * when the run has no score to share. `shareScore`: the headline number in the
 * units of lib/challenge.ts SHARE_SCORES (null: share without a score). When
 * the page came from a friend's shared score, both are shown side by side.
 */
export function ResultFooter({
  test,
  onRetry,
  placement,
  inApp,
  shareLine,
  shareScore = null,
  shareSeed = null,
  shareTraits = null,
  shareLabel,
  appContent,
  children,
}: {
  test: FreeTestId;
  onRetry: () => void;
  placement: string;
  inApp: boolean;
  shareLine: string | null;
  shareScore?: number | null;
  /** The run's seed (typing text, aim targets): the shared link gives a friend the same one. */
  shareSeed?: number | null;
  /** Shown under the buttons, above the app block (e.g. a mode switch). */
  children?: ReactNode;
  /** A profile for the link instead of a score (Big Five). */
  shareTraits?: string | null;
  shareLabel?: string;
  /** An activity-specific app invitation, replacing the generic test block. */
  appContent?: ReactNode;
}) {
  const { t } = useT();
  const embed = useEmbed();
  const score = shareScore === null || !Number.isFinite(shareScore) ? null : Number(shareScoreParam(test, shareScore));
  return (
    <>
      <ChallengeCompare mine={score} />
      <div className="mt-8 flex flex-wrap items-start justify-center gap-3">
        {shareLine && <ShareButton test={test} line={shareLine} score={score} seed={shareSeed} traits={shareTraits} label={shareLabel} />}
        {embed && !shareLine && <EmbedResultLink test={test} score={score} seed={shareSeed} traits={shareTraits} />}
        <button data-interaction="retry" data-result-action="retry" type="button" onClick={onRetry} className={SECONDARY_BUTTON}>
          {t("common.try_again")}
        </button>
      </div>
      {children}
      {!embed && (appContent === undefined ? <AppBlock test={test} placement={placement} inApp={inApp} /> : <><SaveProgress test={test} /><div data-result-action="app">{appContent}</div></>)}
    </>
  );
}

/**
 * Timing tests can't be paused: leaving the tab or window while `active`
 * calls `onInterrupt` (the clock keeps running while hidden, so later times
 * would be meaningless). Same rule as the app's runners.
 */
export function useInterruptOnLeave(active: boolean, onInterrupt: () => void) {
  const cb = useRef(onInterrupt);
  cb.current = onInterrupt;
  useEffect(() => {
    if (!active) return;
    const interrupt = () => { interactions.pause(); cb.current(); };
    const onVisibility = () => {
      if (document.visibilityState !== "visible") interrupt();
    };
    window.addEventListener("blur", interrupt);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("blur", interrupt);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [active]);
}

/**
 * Did the visitor press `letter` (a test's answer key)? The typed character,
 * or, for a Latin letter, the physical key: with a Korean, Russian or Arabic
 * layout active, the S key types "ㄴ"/"ы"/"س", but its code is still KeyS.
 */
export function isKey(e: KeyboardEvent, letter: string): boolean {
  const l = letter.toLowerCase();
  if (!l) return false;
  if (e.key.toLowerCase() === l) return true;
  if (/^[a-z]$/.test(l)) return e.code === `Key${l.toUpperCase()}`;
  if (/^[0-9]$/.test(l)) return e.code === `Digit${l}` || e.code === `Numpad${l}`;
  return false;
}

/** Space / Enter respond while `active` (the app's CognitiveButton.web.tsx). */
export function useResponseKey(active: boolean, respond: () => void) {
  const cb = useRef(respond);
  cb.current = respond;
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Space" && event.key !== " " && event.key !== "Enter") return;
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.isComposing) return;
      const target = event.target instanceof Element ? event.target : null;
      const inResponseArea = target?.closest("[data-response-area]");
      if (!inResponseArea && target?.closest("input, textarea, select, button, a, [contenteditable='true']")) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!event.repeat) cb.current();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [active]);
}

/** The tap/click surface of a running test (pointer-down, like the app's onPressIn). */
export function ResponseArea({
  onRespond,
  areaRef,
  label,
  className,
  children,
}: {
  onRespond: () => void;
  areaRef?: React.Ref<HTMLDivElement>;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div data-interaction="response" data-answer-keys="Space Enter"
      ref={areaRef}
      data-response-area
      role="button"
      tabIndex={0}
      aria-label={label}
      onPointerDown={(e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        e.preventDefault();
        onRespond();
      }}
      onContextMenu={(e) => e.preventDefault()}
      className={cn(
        "flex cursor-pointer touch-manipulation select-none flex-col items-center justify-center outline-none",
        className,
      )}
      style={{ WebkitTapHighlightColor: "transparent", WebkitUserSelect: "none" }}
    >
      {children}
    </div>
  );
}

export function CancelBar({ onCancel }: { onCancel: () => void }) {
  const { t } = useT();
  return (
    <div className="border-t border-border py-3 text-center">
      <button data-interaction="cancel"
        type="button"
        onClick={() => { interactions.pause(); onCancel(); }}
        className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {t("common.cancel")}
      </button>
    </div>
  );
}

/** 3-2-1 lead-in before a stream of trials (the app's CountdownOverlay). */
export function Countdown({ onDone }: { onDone: () => void }) {
  const [n, setN] = useState(3);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (n <= 0) {
      done.current();
      return;
    }
    const t = setTimeout(() => setN(n - 1), 1000);
    return () => clearTimeout(t);
  }, [n]);
  return (
    <div className="flex min-h-[380px] items-center justify-center sm:min-h-[440px]" aria-live="assertive">
      <span className="text-7xl font-bold text-foreground">{n > 0 ? n : ""}</span>
    </div>
  );
}

export function scrollCardIntoView(el: HTMLElement | null) {
  requestAnimationFrame(() => el?.scrollIntoView({ block: "start", behavior: "smooth" }));
}

/** A random whole number from 0 to n - 1. */
export const randInt = (n: number): number => Math.floor(Math.random() * n);

/** A shuffled copy (Fisher–Yates). */
export function shuffled<T>(items: readonly T[]): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = randInt(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** `count` different cells out of 0..cells-1, in random order. */
export const pickCells = (cells: number, count: number): number[] =>
  shuffled(Array.from({ length: cells }, (_, i) => i)).slice(0, count);

/** Remaining lives / strikes as dots (screen readers get the words). */
export function LivesDots({ left, total, label }: { left: number; total: number; label: string }) {
  const { t } = useT();
  return (
    <span className="inline-flex items-center gap-1.5" aria-label={t("common.lives_aria", { label, left, total })}>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          aria-hidden
          className={cn("h-3 w-3 rounded-full", i < left ? "bg-primary" : "border-2 border-border")}
        />
      ))}
    </span>
  );
}

/**
 * True when this browser has finished `test` before (read after mount, so
 * server and first client render agree). Returning visitors start with the
 * real test; first-timers with the practice.
 */
export function useReturningVisitor(test: FreeTestId): boolean {
  const [returning, setReturning] = useState(false);
  useEffect(() => setReturning(finishedRuns(test) > 0), [test]);
  return returning;
}

/**
 * Start buttons of a test with a practice round: first-timers get "Start
 * practice" with "Skip practice" one tap away; returning visitors get "Start
 * the test" with "Practice first".
 */
export function StartChoice({
  returning,
  onPractice,
  onSkip,
}: {
  returning: boolean;
  onPractice: () => void;
  onSkip: () => void;
}) {
  const { t } = useT();
  return (
    <div className="mt-8 flex flex-col items-center gap-3">
      <button data-interaction={returning ? "start" : "practice"} type="button" onClick={returning ? onSkip : onPractice} className={PRIMARY_BUTTON}>
        {t(returning ? "common.start_test" : "common.start_practice")}
      </button>
      <button data-interaction={returning ? "practice" : "start"}
        type="button"
        onClick={returning ? onPractice : onSkip}
        className="rounded-full px-4 py-2 font-semibold text-primary transition-colors hover:bg-muted"
      >
        {t(returning ? "common.practice_first" : "common.skip_practice")}
      </button>
    </div>
  );
}

/** "1:05" / "0:07" for a time left or spent, in whole seconds (rounded up when counting down). */
export function clock(ms: number, roundUp = false): string {
  const s = Math.max(0, roundUp ? Math.ceil(ms / 1000) : Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * On-screen number pad for typed answers (big touch targets, no phone
 * keyboard covering the screen). Keys respond on pointer-down like the other
 * response buttons; Enter/Space on a focused key works too. `keys` are the
 * labels in reading order, three per row; "back" and "clear" are special.
 */
export function Keypad({
  keys,
  onKey,
  disabled = false,
  className,
}: {
  keys: string[];
  onKey: (key: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useT();
  const label = (k: string) => (k === "back" ? "⌫" : k === "clear" ? "C" : k);
  const aria = (k: string) => (k === "back" ? t("common.key_delete") : k === "clear" ? t("common.key_clear") : k);
  return (
    // Digits keep their phone-keypad order in right-to-left pages too.
    <div dir="ltr" className={cn("grid w-full max-w-xs grid-cols-3 gap-2", className)} role="group" aria-label={t("common.keypad")}>
      {keys.map((k) => (
        <button data-interaction={"key:" + k} data-answer-keys={k === "back" ? "Backspace" : k === "clear" ? "Delete" : k}
          key={k}
          type="button"
          disabled={disabled}
          aria-label={aria(k)}
          onPointerDown={(e) => {
            if (e.pointerType === "mouse" && e.button !== 0) return;
            e.preventDefault();
            onKey(k);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onKey(k);
            }
          }}
          className={cn(
            "flex h-14 touch-manipulation select-none items-center justify-center rounded-xl border border-border bg-card text-2xl font-semibold tabular-nums text-foreground transition-colors active:bg-muted disabled:opacity-50 sm:h-12",
            (k === "back" || k === "clear") && "text-muted-foreground",
          )}
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          {label(k)}
        </button>
      ))}
    </div>
  );
}
